import React, { useState, useEffect } from 'react';
import {
    Row, Col, Form, Label, Input, Card, CardBody,
    Container, Button, FormFeedback
} from "reactstrap";
import { toast } from "react-toastify";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchVehiclesBodyTypesEditData } from "../../store/vehicles-bodytypes/actions";
import "./CustomCheckbox.css";

const VehicleBodyTypesEdit = () => {
    const { id } = useParams();
    const dispatch = useDispatch();
    const { data, loading, error } = useSelector(state => state.vehicleBodyTypes.edit);

    const [title, setTitle] = useState("");
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [checkboxes, setCheckboxes] = useState({
        is_active: false,
    });

    const navigate = useNavigate();

    useEffect(() => {
        if (id) {
            dispatch(fetchVehiclesBodyTypesEditData(id));
        }
    }, [dispatch, id]);

    useEffect(() => {
        if (data?.data) {
            setTitle(data.data.name || "");
            setImagePreview(data.data.image || null);
            setCheckboxes({
                is_active: data.data.is_active ?? false,
            });
        }
    }, [data]);

    const [validation, setValidation] = useState({
        title: null,
        image: null,
    });

    const handleCheckboxChange = (key) => {
        setCheckboxes((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImageFile(file);
            const previewUrl = URL.createObjectURL(file);
            setImagePreview(previewUrl);
        } else {
            setImageFile(null);
            setImagePreview(null);
        }
    };

    const validateFields = () => {
        let isValid = true;
        const newValidation = {};

        newValidation.title = title.trim() !== '';

        setValidation(newValidation);

        Object.values(newValidation).forEach(v => {
            if (v !== true) isValid = false;
        });

        return isValid;
    };

    // Submit handler
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateFields()) return;

        const formData = new FormData();
        formData.append('id', id);
        formData.append('title', title);
        formData.append('is_active', checkboxes.is_active);
        if (imageFile) formData.append('image', imageFile);

        try {
            const token = localStorage.getItem("authToken");
            const response = await fetch(`${process.env.REACT_APP_WEB_APP_URL}/api/web/vehicles-bodytypes/update`, {
                method: "POST",
                headers: {
                    "Accept": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
                body: formData,
            });

            const result = await response.json();

            if (response.status === 200) {
                toast.success('Vehicle Body-Types updated successfully', { autoClose: 3000 });
                navigate(`/vehicles-bodytypes`);
            } else if (response.status === 422) {
                if (result?.errors) {
                    Object.values(result.errors).forEach((errorArr) => {
                        errorArr.forEach((errMsg) => {
                            toast.error(errMsg, { autoClose: 3000 });
                        });
                    });
                } else {
                    toast.error(result?.message || 'Validation failed', { autoClose: 3000 });
                }
            } else {
                toast.error(result?.message || `Error: ${response.statusText}`, { autoClose: 3000 });
            }
        } catch (err) {
            console.log(err);
            toast.error("Failed to update vehicle body-types", { autoClose: 3000 });
        }
    };

    document.title = "Edit Vehicles Body-Types | Moeen";

    return (
        <div className="page-content">
            <Container fluid={true}>
                <Breadcrumbs maintitle="Home" title="Vehicles Body-Types" breadcrumbItem="Edit Vehicles Body-Types" />

                <Row>
                    <Col lg={12}>
                        <Card>
                            <CardBody>
                                <Form onSubmit={handleSubmit}>
                                    <Row>
                                        <Col md="6">
                                            {/* Title */}
                                            <div className="mb-3">
                                                <Label>Title</Label>
                                                <Input
                                                    type="text"
                                                    placeholder="Enter title"
                                                    value={title}
                                                    onChange={e => setTitle(e.target.value)}
                                                    valid={validation["title"] === true}
                                                    invalid={validation["title"] !== true && validation["title"] !== null}
                                                />
                                                <FormFeedback tooltip>Material Name is required.</FormFeedback>
                                            </div>
                                        </Col>

                                        <Col md="6">
                                            {/* Image */}
                                            <div className="mb-3">
                                                <Label>Image</Label>
                                                <Input
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={handleImageChange}
                                                />

                                                {imagePreview && (
                                                    <div style={{ marginTop: '10px' }}>
                                                        <img
                                                            src={imagePreview}
                                                            alt="Preview"
                                                            style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: 4, border: '1px solid #ddd' }}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        </Col>

                                        <Col md="6">
                                            {/* Checkbox */}
                                            <div className="d-flex flex-wrap gap-2">
                                                {[
                                                    { label: "Is Active", key: "is_active" },
                                                ].map(({ label, key }) => (
                                                    <div
                                                        key={key}
                                                        className={`custom-box-checkbox ${checkboxes[key] ? "active" : ""}`}
                                                        onClick={() => handleCheckboxChange(key)}
                                                    >
                                                        {label}
                                                    </div>
                                                ))}
                                            </div>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col className="text-end">
                                            <Button type="submit" color="primary">
                                                {loading ? (
                                                    <>
                                                        <span
                                                            className="spinner-border spinner-border-sm me-2"
                                                            role="status"
                                                            aria-hidden="true"
                                                        ></span>
                                                        Updating
                                                    </>
                                                ) : (
                                                    "Update"
                                                )}
                                            </Button>
                                        </Col>
                                    </Row>
                                </Form>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default VehicleBodyTypesEdit;
